import React, { useCallback, useEffect, useState, type MouseEvent } from "react";
import {
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { useStyles } from "../../assets/styles/Theme.tsx";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import AppDialog from "../../components/common/AppDialog.tsx";
import {
  CON_MIGRATION_DEFAULT_USER,
  CON_MIGRATION_DEFAULT_VARIANT_VALUE,
  CON_MIGRATION_SYSTEM_FIELDS,
  type SystemField,
} from "../../utils/sys/Constants.tsx";
import {
  getDuplicateStatusMessageForRow,
  renderImportStatusIcon,
} from "./importStatusIcon.tsx";
import { hasMigrationSession } from "../../utils/items/dataMigrationSession.ts";
import {
  MSG_IMPORT_COMPLETE,
  MSG_ITEM_RECORD_EXISTS,
  MSG_NO_ERROR_RECORDS,
  MSG_ERROR_REPORT_FAILED,
  MSG_UNABLE_SAVE_CHANGES,
  MSG_UNABLE_DELETE_RECORD,
  MSG_SUBMISSION_FAILED,
  MSG_UNIT_PER_PACKAGE_MUST_BE_NUMBER,
  MSG_RECORD_REMOVED_SUCCESS,
} from "../../utils/sys/Messages.tsx";
import {
  deleteStagingRow,
  getUnverifiedStagingRows,
  parseApiError,
  StagingRow,
  submitStagingRow,
  updateStagingRow,
} from "../../services/itemImportStaging.ts";

interface UserParam {
  COMPCODE?: string;
  STAFFMAPLOCODE?: string;
  USERNAME?: string;
}

interface EditVariantPair {
  id: number;
  variantname: string;
  variantvalue: string;
}

interface VariantPairValues {
  variantname: string;
  variantvalue: string;
}

interface ItemTypeState {
  sellingItem: boolean;
  inventoryItem: boolean;
}

let nextVariantPairId = 1;

const MSG_ITEM_TYPE_REQUIRED =
  "Please select 'Yes' for at least one option: 'Selling Item' or 'Inventory Item.'";

const createEmptyVariantPair = (): EditVariantPair => ({
  id: nextVariantPairId++,
  variantname: "",
  variantvalue: "",
});

const parseVariantRaw = (variantdata?: string): VariantPairValues[] => {
  if (!variantdata || !String(variantdata).trim()) {
    return [{ variantname: "", variantvalue: "" }];
  }
  try {
    const parsed = JSON.parse(variantdata);
    if (Array.isArray(parsed) && parsed.length > 0) {
      // ✅ Check if it's the default "no variant" payload generated by the backend
      if (
        parsed.length === 1 &&
        parsed[0].variantname === "" &&
        parsed[0].variantvalue === CON_MIGRATION_DEFAULT_VARIANT_VALUE
      ) {
        return [{ variantname: "", variantvalue: "" }];
      }

      return parsed.map((item: any) => ({
        variantname: String(item?.variantname ?? item?.variantName ?? "").trim(),
        variantvalue: String(item?.variantvalue ?? item?.variantValue ?? "").trim(),
      }));
    }
  } catch {
    // Fall through for plain "Name: Value" display strings
  }
  const trimmed = String(variantdata).trim();
  if (trimmed.includes(":")) {
    const parts = trimmed.split(";").map((s) => s.trim()).filter(Boolean);
    if (parts.length > 0) {
      return parts.map((part) => {
        const idx = part.indexOf(":");
        if (idx === -1) {
          return { variantname: "", variantvalue: part };
        }
        return {
          variantname: part.slice(0, idx).trim(),
          variantvalue: part.slice(idx + 1).trim(),
        };
      });
    }
  }
  return [{ variantname: "", variantvalue: trimmed }];
};

const parseVariantPairs = (variantdata?: string): EditVariantPair[] =>
  parseVariantRaw(variantdata).map((p) => ({
    id: nextVariantPairId++,
    variantname: p.variantname,
    variantvalue: p.variantvalue,
  }));

const serializeVariantPairs = (pairs: EditVariantPair[]): string => {
  const filled = pairs
    .map((p) => ({
      variantname: (p.variantname || "").trim(),
      variantvalue: (p.variantvalue || "").trim(),
    }))
    .filter((p) => p.variantname !== "" || p.variantvalue !== "");

  if (filled.length === 0) {
    return JSON.stringify([
      {
        variantcode: 0,
        variantname: "",
        variantvalue: CON_MIGRATION_DEFAULT_VARIANT_VALUE,
      },
    ]);
  }

  return JSON.stringify(
    filled.map((p, i) => ({
      variantcode: i + 1,
      variantname: p.variantname,
      variantvalue: p.variantvalue,
    }))
  );
};

// ✅ Helper: consistently format a boolean field for export as "Yes" or "No"
const formatYN = (value: unknown): string => {
  if (value === undefined || value === null || String(value).trim() === "") return "";
  const s = String(value).trim().toUpperCase();
  if (s === "Y" || s === "YES" || s === "TRUE" || s === "1") return "Yes";
  if (s === "N" || s === "NO" || s === "FALSE" || s === "0") return "No";
  return String(value).trim();
};

// ✅ Helper: render variantdata in a readable "Name: Value; Name: Value" form
const formatVariantDataForExport = (variantdata?: string): string => {
  const pairs = parseVariantRaw(variantdata);
  const cleaned = pairs
    .filter((p) => (p.variantname || "").trim() || (p.variantvalue || "").trim())
    .map((p) => `${p.variantname || ""}: ${p.variantvalue || ""}`);
  return cleaned.join("; ");
};

const UnverifiedItems: React.FC = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const showReturnToMigration =
    (location.state as { fromDataMigration?: boolean })?.fromDataMigration === true ||
    hasMigrationSession();
  const SYS_USER = useSelector((state: any) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = (SYS_USER.USER_PARAM as UserParam) || {};

  const [rows, setRows] = useState<StagingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "info">("info");
  const [alertMessage, setAlertMessage] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<StagingRow | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [editVariants, setEditVariants] = useState<EditVariantPair[]>([createEmptyVariantPair()]);
  const [editItemType, setEditItemType] = useState<ItemTypeState>({
    sellingItem: false,
    inventoryItem: false,
  });
  const [itemTypeError, setItemTypeError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<StagingRow | null>(null);
  const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; row: StagingRow } | null>(
    null
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const bodyFontSize =
    (theme.typography as { body_s?: { fontSize?: string } }).body_s?.fontSize || "0.875rem";
  const dividerColor = theme.palette.divider || "rgb(224, 224, 224)";
  const headerCellSx = {
    fontWeight: 600,
    textTransform: "capitalize" as const,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: bodyFontSize,
    lineHeight: "normal",
    letterSpacing: "normal",
    height: 56,
    maxHeight: 56,
    py: 0,
    px: "10px",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle" as const,
    borderBottom: `1px solid ${dividerColor}`,
  };
  const bodyCellSx = {
    fontSize: bodyFontSize,
    fontWeight: 400,
    lineHeight: "normal",
    color: theme.palette.text.primary,
    height: 52,
    maxHeight: 52,
    py: 0,
    px: "10px",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle" as const,
    borderBottom: `1px solid ${dividerColor}`,
  };

  const loadRows = useCallback(async () => {
    if (!COMPCODE || !STAFFMAPLOCODE) return;
    setLoading(true);
    try {
      const data = await getUnverifiedStagingRows(
        COMPCODE,
        STAFFMAPLOCODE,
        USERNAME || CON_MIGRATION_DEFAULT_USER
      );
      const normalized = (data || []).map((r: any) => ({
        stagingcode: r.stagingcode ?? r.STAGINGCODE,
        refno: r.refno ?? r.REFNO,
        batchcode: r.batchcode ?? r.BATCHCODE,
        brandname: r.brandname ?? r.BRANDNAME,
        maincategory: r.maincategory ?? r.MAINCATEGORY,
        category: r.category ?? r.CATEGORY,
        subcategory: r.subcategory ?? r.SUBCATEGORY,
        genericname: r.genericname ?? r.GENERICNAME,
        itemname: r.itemname ?? r.ITEMNAME,
        packagetype: r.packagetype ?? r.PACKAGETYPE,
        measuringunit: r.measuringunit ?? r.MEASURINGUNIT,
        modelname: r.modelname ?? r.MODELNAME,
        packageunits: r.packageunits ?? r.PACKAGEUNITS,
        descrip: r.descrip ?? r.DESCRIP,
        isselling: r.isselling ?? r.ISSELLING,
        isinv: r.isinv ?? r.ISINV,
        variantdata: r.variantdata ?? r.VARIANTDATA,
        recordstatus: r.recordstatus ?? r.RECORDSTATUS,
        validationerrors: r.validationerrors ?? r.VALIDATIONERRORS,
        duplicatesource: r.duplicatesource ?? r.DUPLICATESOURCE,
        sourcerowno: r.sourcerowno ?? r.SOURCEROWNO,
        sellingitem: r.sellingitem ?? r.SELLINGITEM,
        inventoryitem: r.inventoryitem ?? r.INVENTORYITEM,
      }));
      setRows(normalized);
    } catch (error) {
      setAlertType("error");
      setAlertMessage(parseApiError(error));
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  }, [COMPCODE, STAFFMAPLOCODE, USERNAME]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const getEditFormErrors = (form: Record<string, string>): Record<string, string> => {
    const nextErrors: Record<string, string> = {};
    const fields = Object.values(CON_MIGRATION_SYSTEM_FIELDS) as SystemField[];

    fields.forEach((field) => {
      const value = (form[field.apiField] || "").trim();

      if (field.key === "unitPerPackage") {
        if (!value) {
          nextErrors[field.apiField] = `${field.label} is required`;
        } else if (!/^[1-9]\d*$/.test(value)) {
          nextErrors[field.apiField] = MSG_UNIT_PER_PACKAGE_MUST_BE_NUMBER;
        }
        return;
      }

      if (field.mandatory && !value) {
        nextErrors[field.apiField] = `${field.label} is required`;
      }
    });

    return nextErrors;
  };

  const resolveRowItemType = (row: StagingRow): ItemTypeState => {
    const toChecked = (v: unknown): boolean => {
      const s = String(v ?? "").trim().toUpperCase();
      return s === "Y" || s === "YES";
    };
    return {
      sellingItem: toChecked(row.isselling),
      inventoryItem: toChecked(row.isinv),
    };
  };

  const handleEditOpen = (row: StagingRow) => {
    const initialForm = {
      brandname: row.brandname || "",
      maincategory: row.maincategory || "",
      category: row.category || "",
      subcategory: row.subcategory || "",
      genericname: row.genericname || "",
      itemname: row.itemname || "",
      packagetype: row.packagetype || "",
      measuringunit: row.measuringunit || "",
      modelname: row.modelname || "",
      packageunits: row.packageunits || "",
      descrip: row.descrip || "",
    };

    setEditRow(row);
    setEditForm(initialForm);
    setEditErrors(getEditFormErrors(initialForm));
    setEditVariants(parseVariantPairs(row.variantdata));
    setEditItemType(resolveRowItemType(row));
    setItemTypeError("");
    setEditOpen(true);
  };

  const handleEditFieldChange = (apiField: string, value: string) => {
    if (apiField === "packageunits" && value !== "" && !/^\d*$/.test(value)) {
      return;
    }
    setEditForm((prev) => ({ ...prev, [apiField]: value }));
    if (editErrors[apiField]) {
      setEditErrors((prev) => {
        const next = { ...prev };
        delete next[apiField];
        return next;
      });
    }
  };

  const handleItemTypeChange = (key: keyof ItemTypeState, checked: boolean) => {
    setEditItemType((prev) => {
      const next = { ...prev, [key]: checked };
      if (next.sellingItem || next.inventoryItem) {
        setItemTypeError("");
      }
      return next;
    });
  };

  const validateEditForm = (): boolean => {
    const nextErrors = getEditFormErrors(editForm);
    setEditErrors(nextErrors);

    const hasItemTypeError = !editItemType.sellingItem && !editItemType.inventoryItem;
    setItemTypeError(hasItemTypeError ? MSG_ITEM_TYPE_REQUIRED : "");

    return Object.keys(nextErrors).length === 0 && !hasItemTypeError;
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditErrors({});
    setItemTypeError("");
    setEditItemType({ sellingItem: false, inventoryItem: false });
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    if (!validateEditForm()) return;

    // ✅ Detect whether the user actually changed anything.
    // If nothing changed, we preserve the original duplicate classification
    // and show the original error message without relying on the backend re-evaluation.
    const originalForm = {
      brandname: editRow.brandname || "",
      maincategory: editRow.maincategory || "",
      category: editRow.category || "",
      subcategory: editRow.subcategory || "",
      genericname: editRow.genericname || "",
      itemname: editRow.itemname || "",
      packagetype: editRow.packagetype || "",
      measuringunit: editRow.measuringunit || "",
      modelname: editRow.modelname || "",
      packageunits: String(editRow.packageunits || ""),
      descrip: editRow.descrip || "",
    };

    const isFormUnchanged =
      Object.keys(originalForm).every(
        (key) => (originalForm[key as keyof typeof originalForm] || "").trim() ===
                 (editForm[key] || "").trim()
      ) &&
      editItemType.sellingItem === resolveRowItemType(editRow).sellingItem &&
      editItemType.inventoryItem === resolveRowItemType(editRow).inventoryItem &&
      serializeVariantPairs(editVariants) === serializeVariantPairs(parseVariantPairs(editRow.variantdata));

    // ✅ If no changes were made AND the original was a duplicate,
    // keep the original status and message — do not call the API.
    if (isFormUnchanged && editRow.recordstatus === "DUPLICATE") {
      handleEditClose();
      setAlertType("error");
      setAlertMessage(getDuplicateStatusMessageForRow(editRow));
      setAlertOpen(true);
      return;
    }

    try {
      const updated = await updateStagingRow(editRow.refno, {
        compcode: COMPCODE,
        loccode: STAFFMAPLOCODE,
        enuser: USERNAME || CON_MIGRATION_DEFAULT_USER,
        brandname: editForm.brandname,
        maincategory: editForm.maincategory,
        category: editForm.category,
        subcategory: editForm.subcategory,
        genericname: editForm.genericname,
        itemname: editForm.itemname,
        packagetype: editForm.packagetype,
        measuringunit: editForm.measuringunit,
        modelname: editForm.modelname,
        packageunits: editForm.packageunits,
        descrip: editForm.descrip,
        isselling: editItemType.sellingItem ? "Y" : "N",
        isinv: editItemType.inventoryItem ? "Y" : "N",
        variantdata: serializeVariantPairs(editVariants),
      });

      handleEditClose();
      const newStatus = (updated as any)?.recordstatus ?? (updated as any)?.RECORDSTATUS;
      if (newStatus === "DUPLICATE") {
        setAlertType("error");
        setAlertMessage(
          getDuplicateStatusMessageForRow({
            duplicatesource:
              (updated as any)?.duplicatesource ??
              (updated as any)?.DUPLICATESOURCE ??
              editRow.duplicatesource,
            validationerrors:
              (updated as any)?.validationerrors ??
              (updated as any)?.VALIDATIONERRORS ??
              editRow.validationerrors,
          })
        );
      } else if (newStatus === "INVALID") {
        setAlertType("error");
        setAlertMessage("Record still has validation errors.");
      } else {
        setAlertType("success");
        setAlertMessage("Record updated successfully.");
      }
      setAlertOpen(true);
      loadRows();
    } catch (error) {
      setAlertType("error");
      setAlertMessage(parseApiError(error) || MSG_UNABLE_SAVE_CHANGES);
      setAlertOpen(true);
    }
  };

  const handleSubmit = async (row: StagingRow) => {
    if (row.recordstatus === "DUPLICATE") {
      setAlertType("error");
      setAlertMessage(getDuplicateStatusMessageForRow(row));
      setAlertOpen(true);
      return;
    }
    if (row.recordstatus === "INVALID") {
      setAlertType("error");
      setAlertMessage("Please correct validation errors before submitting.");
      setAlertOpen(true);
      return;
    }
    try {
      await submitStagingRow(row.refno, {
        compcode: COMPCODE,
        loccode: STAFFMAPLOCODE,
        enuser: USERNAME || CON_MIGRATION_DEFAULT_USER,
      });
      setAlertType("success");
      setAlertMessage(MSG_IMPORT_COMPLETE);
      setAlertOpen(true);
      loadRows();
    } catch (error: any) {
      const msg = parseApiError(error);
      setAlertType("error");
      setAlertMessage(
        msg.includes("exists") || msg === MSG_ITEM_RECORD_EXISTS
          ? getDuplicateStatusMessageForRow(row)
          : msg || MSG_SUBMISSION_FAILED
      );
      setAlertOpen(true);
      loadRows();
    }
  };

  const handleDelete = async (row: StagingRow) => {
    try {
      await deleteStagingRow(
        row.refno,
        COMPCODE!,
        STAFFMAPLOCODE!,
        USERNAME || CON_MIGRATION_DEFAULT_USER
      );
      setAlertType("success");
      setAlertMessage(MSG_RECORD_REMOVED_SUCCESS);
      setAlertOpen(true);
      loadRows();
    } catch (error) {
      setAlertType("error");
      setAlertMessage(parseApiError(error) || MSG_UNABLE_DELETE_RECORD);
      setAlertOpen(true);
    }
  };

  const openDeleteConfirm = (row: StagingRow) => {
    setRowToDelete(row);
    setOpenDeleteDialog(true);
  };

  const cancelDelete = () => {
    setRowToDelete(null);
    setOpenDeleteDialog(false);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    const row = rowToDelete;
    cancelDelete();
    await handleDelete(row);
  };

  const formatStagingErrorDetails = (row: StagingRow): string => {
    if (row.recordstatus === "DUPLICATE") {
      return getDuplicateStatusMessageForRow(row);
    }
    if (!row.validationerrors) return "";
    try {
      const parsed = JSON.parse(row.validationerrors);
      if (Array.isArray(parsed)) {
        return parsed
          .map((e) =>
            typeof e === "string" ? e : `${e.field || "Error"}: ${e.message || String(e)}`
          )
          .join("; ");
      }
      return String(parsed);
    } catch {
      return row.validationerrors;
    }
  };

  /**
   * ✅ FIXED: Download error report
   *
   * Root cause of the mismatch: previously, the headers came from
   * `Object.values(CON_MIGRATION_SYSTEM_FIELDS).map(f => f.label)` while
   * the row data was a hand-written array whose order didn't match. When
   * `CON_MIGRATION_SYSTEM_FIELDS` contained `isselling`/`isinv`, every
   * column after that point was shifted.
   *
   * Fix: build headers AND values from the SAME filtered `exportFields`
   * array so alignment is guaranteed by construction.
   *
   * Additional improvements:
   * - Include `Selling Item`, `Inventory Item`, and `Variant Data`
   *   (both readable and raw JSON) so users can correct and re-upload.
   * - Put `Reference No., Item Name, Brand Name, Main Category, Category,
   *   Subcategory` first to match the on-screen grid.
   * - Auto-size Excel columns.
   * - Date-stamp the filename.
   * - Log errors so silent failures are diagnosable.
   */
  const downloadErrorReport = () => {
    try {
      const errorRows = rows.filter(
        (r) => r.recordstatus === "INVALID" || r.recordstatus === "DUPLICATE"
      );
      if (errorRows.length === 0) {
        setAlertType("info");
        setAlertMessage(MSG_NO_ERROR_RECORDS);
        setAlertOpen(true);
        return;
      }

      // ✅ Exclude isselling/isinv from the loop since they render as dedicated columns below.
      const exportFields = (Object.values(CON_MIGRATION_SYSTEM_FIELDS) as SystemField[])
        .filter((field) => field.apiField !== "isselling" && field.apiField !== "isinv");

      // ✅ Core identifier fields are duplicated at the front of the export
      //    so column order matches the UI grid.
      const coreFieldApis = ["itemname", "brandname", "maincategory", "category", "subcategory"];

      // ✅ Dynamically determine the maximum number of variant pairs across all error rows
      //    so we can create separate columns (Variant 1 Name, Variant 1 Value, etc.).
      let maxVariantPairs = 0;
      const parsedRowsVariants = errorRows.map((r) => {
        const pairs = parseVariantRaw(r.variantdata);
        if (pairs.length > maxVariantPairs) {
          maxVariantPairs = pairs.length;
        }
        return pairs;
      });

      // Default to at least 1 variant column to match the import template structure if empty
      if (maxVariantPairs === 0) maxVariantPairs = 1;

      const variantHeaders: string[] = [];
      for (let i = 0; i < maxVariantPairs; i++) {
        variantHeaders.push(`Variant ${i + 1} Name`, `Variant ${i + 1} Value`);
      }

      const headers: string[] = [
        "Reference No.",
        "Item Name",
        "Brand Name",
        "Item Main Category",
        "Item Category",
        "Item Sub Category",
        ...exportFields
          .filter((f) => !coreFieldApis.includes(f.apiField))
          .map((f) => f.label),
        "Selling Item",
        "Inventory Item",
        ...variantHeaders, // Spread the newly created variant columns here
        "Status",
        "Error Details",
      ];

      const data = errorRows.map((r, rowIndex) => {
        const fieldValues: Record<string, string> = {
          brandname: r.brandname || "",
          maincategory: r.maincategory || "",
          category: r.category || "",
          subcategory: r.subcategory || "",
          genericname: r.genericname || "",
          itemname: r.itemname || "",
          measuringunit: r.measuringunit || "",
          packagetype: r.packagetype || "",
          modelname: r.modelname || "",
          packageunits: r.packageunits || "",
          descrip: r.descrip || "",
        };

        const row: (string | number)[] = [
          r.refno,
          r.itemname || "",
          r.brandname || "",
          r.maincategory || "",
          r.category || "",
          r.subcategory || "",
        ];

        // ✅ Append remaining system fields using the SAME order as headers.
        exportFields
          .filter((f) => !coreFieldApis.includes(f.apiField))
          .forEach((f) => {
            row.push(fieldValues[f.apiField] || "");
          });

        row.push(formatYN(r.isselling));
        row.push(formatYN(r.isinv));

        // ✅ Flatten the variants into separate columns
        const rowVariants = parsedRowsVariants[rowIndex];
        for (let i = 0; i < maxVariantPairs; i++) {
          const variant = rowVariants[i];
          row.push(variant ? variant.variantname : "");
          row.push(variant ? variant.variantvalue : "");
        }

        row.push(r.recordstatus);
        row.push(formatStagingErrorDetails(r));

        return row;
      });

      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

      // ✅ Auto-size columns based on the longest cell per column.
      const colWidths = headers.map((h, colIdx) => {
        const maxContentLength = Math.max(
          h.length,
          ...data.map((row) => String(row[colIdx] ?? "").length)
        );
        return { wch: Math.min(Math.max(maxContentLength + 2, 10), 60) };
      });
      ws["!cols"] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Error Rows");

      const filename = `Item_Import_Error_Report_${new Date()
        .toISOString()
        .split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error("Error generating item import error report:", err);
      setAlertType("error");
      setAlertMessage(MSG_ERROR_REPORT_FAILED);
      setAlertOpen(true);
    }
  };

  /**
   * ✅ Renders the status icon for a row, wrapped in a Tooltip whenever there
   * is an error/warning message to show. This is the ONLY place the warning
   * message tooltip is rendered — hovering the icon in the status column shows
   * the warning/duplicate message.
   */
  const renderRowStatus = (row: StagingRow): React.ReactElement => {
    const message = formatStagingErrorDetails(row);
    const icon = renderImportStatusIcon(
      row.recordstatus,
      row.duplicatesource,
      !message,
      row.validationerrors
    );

    if (!message) return icon;

    return (
      <Tooltip title={message} arrow placement="top">
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", cursor: "help" }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={styles.box_add_page_container as any}>
      <SystemAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
        type={alertType}
      />

      <Box sx={styles.box_breadcrumb as any}>
        <IconButton sx={styles.btn_breadcrumb_back as any} onClick={() => navigate("/items")}>
          <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon as any} />
        </IconButton>
        <Breadcrumbs separator="›" sx={{ ...(styles.breadcrumb as any), ml: 1 }}>
          <Typography sx={styles.breadcrumb_link as any}>Item Management</Typography>
          <Typography sx={styles.breadcrumb_link_page as any}>Unverified Items</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ ...(styles.box_page_container as any), width: "100%" }}>
        <AppBar sx={styles.appbar as any}>
          <Box
            sx={{
              ...(styles.appbar_grid_container as any),
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 2,
            }}
          >
            <Typography sx={styles.appbar_title as any}>Unverified Item Records</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              {showReturnToMigration && (
                <Button
                  startIcon={<ArrowForwardIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ height: 40, textTransform: "none" }}
                  onClick={() => navigate("/data-migration")}
                >
                  Return to Data Import
                </Button>
              )}
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                sx={{ height: 40, textTransform: "none" }}
                onClick={downloadErrorReport}
              >
                Download Error Report
              </Button>
            </Box>
          </Box>
        </AppBar>

        <Box sx={{ ...(styles.view_page_container as any), width: "100%" }}>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={styles.VDG_paper as any}>
              <Box sx={{ height: 480, width: "100%", display: "flex", flexDirection: "column" }}>
                <TableContainer sx={{ flex: 1, p: 1 }}>
                  <Table
                    stickyHeader
                    size="medium"
                    sx={{
                      fontSize: bodyFontSize,
                      tableLayout: "fixed",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      "& .MuiTableCell-root": {
                        boxSizing: "border-box",
                      },
                      "& .MuiTableRow-root": {
                        height: 52,
                      },
                      "& .MuiTableHead .MuiTableRow-root": {
                        height: 56,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell width={48} sx={headerCellSx} />
                        <TableCell sx={headerCellSx}>Reference Number</TableCell>
                        <TableCell sx={headerCellSx}>Item Name</TableCell>
                        <TableCell sx={headerCellSx}>Brand Name</TableCell>
                        <TableCell sx={headerCellSx}>Item Main Category</TableCell>
                        <TableCell sx={headerCellSx}>Item Category</TableCell>
                        <TableCell sx={headerCellSx}>Item Sub Category</TableCell>
                        <TableCell align="center" width={90} sx={headerCellSx}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows
                        .slice(page * pageSize, page * pageSize + pageSize)
                        .map((row) => (
                          <TableRow
                            key={row.refno}
                            hover
                            sx={{
                              height: 52,
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <TableCell sx={bodyCellSx}>
                              {renderRowStatus(row)}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>{row.refno}</TableCell>
                            <TableCell sx={bodyCellSx}>{row.itemname}</TableCell>
                            <TableCell sx={bodyCellSx}>{row.brandname}</TableCell>
                            <TableCell sx={bodyCellSx}>{row.maincategory}</TableCell>
                            <TableCell sx={bodyCellSx}>{row.category}</TableCell>
                            <TableCell sx={bodyCellSx}>{row.subcategory}</TableCell>
                            <TableCell align="center" sx={bodyCellSx}>
                              <IconButton
                                aria-label="Actions"
                                onClick={(event: MouseEvent<HTMLElement>) =>
                                  setActionMenu({ anchor: event.currentTarget, row })
                                }
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      {rows.length === 0 && (
                        <TableRow sx={{ height: "auto" }}>
                          <TableCell
                            colSpan={8}
                            align="center"
                            sx={{
                              ...bodyCellSx,
                              height: "auto",
                              maxHeight: "none",
                              py: 6,
                              whiteSpace: "normal",
                              color: "text.secondary",
                            }}
                          >
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={rows.length}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(newPage)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 100]}
                  sx={styles.VDG_pagination as any}
                />
              </Box>

              <Menu
                anchorEl={actionMenu?.anchor}
                open={Boolean(actionMenu)}
                onClose={() => setActionMenu(null)}
                sx={styles.VDG_colActionIconMenu as any}
              >
                {actionMenu &&
                  (() => {
                    const menuRow = actionMenu.row;
                    const isDuplicate = menuRow.recordstatus === "DUPLICATE";
                    const isInvalid = menuRow.recordstatus === "INVALID";
                    const submitDisabled = isDuplicate || isInvalid;
                    return (
                      <>
                        {/* ✅ No MUI Tooltip here — the warning tooltip lives only on the status icon */}
                        <MenuItem
                          disabled={submitDisabled}
                          onClick={() => {
                            setActionMenu(null);
                            handleSubmit(menuRow);
                          }}
                          sx={styles.VDG_colActionIcon as any}
                        >
                          <SendIcon />
                          <span className="menuItemText">Submit</span>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setActionMenu(null);
                            handleEditOpen(menuRow);
                          }}
                          sx={styles.VDG_colActionIcon as any}
                        >
                          <BorderColorOutlinedIcon />
                          <span className="menuItemText">Edit</span>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setActionMenu(null);
                            openDeleteConfirm(menuRow);
                          }}
                          sx={styles.VDG_colActionIcon as any}
                        >
                          <DeleteIcon />
                          <span className="menuItemText">Delete</span>
                        </MenuItem>
                      </>
                    );
                  })()}
              </Menu>
            </Paper>
          )}
        </Box>
      </Box>

      <AppDialog
        open={editOpen}
        onClose={handleEditClose}
        title={`Edit Record ${editRow?.refno}`}
        maxWidth="md"
        fullWidth
        cancelLabel="Cancel"
        confirmLabel="Save Changes"
        confirmColor="primary"
        onConfirm={handleEditSave}
      >
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {(Object.values(CON_MIGRATION_SYSTEM_FIELDS) as SystemField[])
            .filter((field) => field.apiField !== "isselling" && field.apiField !== "isinv")
            .map((field) => {
            const fieldError = editErrors[field.apiField] || "";
            return (
              <Grid item xs={6} key={field.apiField}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  required={field.mandatory}
                  value={editForm[field.apiField] || ""}
                  onChange={(e) => handleEditFieldChange(field.apiField, e.target.value)}
                  error={Boolean(fieldError)}
                  helperText={fieldError}
                  FormHelperTextProps={{
                    sx: { fontSize: "12px", color: "error.main" },
                  }}
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                  }}
                />
              </Grid>
            );
          })}

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              Item Type
            </Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editItemType.sellingItem}
                    onChange={(e) => handleItemTypeChange("sellingItem", e.target.checked)}
                  />
                }
                label="Selling Item"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editItemType.inventoryItem}
                    onChange={(e) => handleItemTypeChange("inventoryItem", e.target.checked)}
                  />
                }
                label="Inventory Item"
              />
            </Box>
            {itemTypeError && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "error.main",
                  mt: 0.5,
                }}
              >
                {itemTypeError}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              Variant Fields
            </Typography>
            {editVariants.map((pair) => (
              <Grid container spacing={2} key={pair.id} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Variant Name"
                    value={pair.variantname}
                    onChange={(e) =>
                      setEditVariants((prev) =>
                        prev.map((p) =>
                          p.id === pair.id ? { ...p, variantname: e.target.value } : p
                        )
                      )
                    }
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Variant Value"
                    value={pair.variantvalue}
                    onChange={(e) =>
                      setEditVariants((prev) =>
                        prev.map((p) =>
                          p.id === pair.id ? { ...p, variantvalue: e.target.value } : p
                        )
                      )
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  {editVariants.length > 1 && (
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove variant"
                        onClick={() =>
                          setEditVariants((prev) => prev.filter((p) => p.id !== pair.id))
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            ))}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setEditVariants((prev) => [...prev, createEmptyVariantPair()])}
            >
              Add Variant
            </Button>
          </Grid>
        </Grid>
      </AppDialog>

      <AppDialog
        open={openDeleteDialog}
        onClose={cancelDelete}
        titleId="unverified-delete-dialog-title"
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default UnverifiedItems;